import Button from "antd/es/button";
import Layout from "antd/es/layout";
import moment from "moment";
import momentTz from "moment-timezone";
import { useEffect, useMemo, useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useDispatch, useSelector } from "react-redux";
import MobileNavbar from "src/components/utils/MobileNavbar";
import message from "antd/es/message";
import { useLiveSchedule } from "src/hooks/useLiveSchedule";
import { useResizeObserver } from "src/hooks/useResizeObserver";
import { getDefaultBookingDate } from "src/weirdStuff/date";
import styled, { keyframes } from "styled-components";
import {
  enterSameDay,
  setDate,
  setFlexibleTime,
  setOccurance,
  setPromocode,
  setTime,
  setZipEditorOpen,
} from "../actions";
import { RootState } from "src";
import { usePricingConfig } from "src/hooks/usePricingConfig";
import {
  currentPricingConfig,
  pricingConfigLoadFailed,
  pricingConfigVersion,
} from "src/lib/pricing-config";
import { logEvent } from "../analytics/amplitude";
import {
  SameDayWindows,
  typesetWindowLabel,
} from "../components/utils/SameDayWindows";
import { disabledTime } from "../components/utils/Timer";
import { ArrivalPicker } from "../components/utils/ArrivalPicker";
import { FEE, SAVINGS } from "../components/utils/savings";
import { ArrowRight, Check, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import { useSameDayAvailability } from "../hooks/useSameDayAvailability";
import {
  computeBookingPrice,
  frequencyLabelFor,
  offersPlans,
  resolveTypeId,
} from "../lib/price";
import { PlanPicker } from "../components/utils/PlanPicker";
import { cleaningTypes, frequencyOptions } from "src/constants/options";
import { useAreaCalendar } from "../hooks/useAreaCalendar";
import { AreaDay, arrivalFor, quoteKey } from "../lib/proximity";
import {
  daySavingBadges,
  cheapestDays,
  percentForTier,
  priceOnDay,
  tierForDay,
  tileSaving,
} from "../lib/day-prices";
import { dayOfWeekForServiceDate, formatCents } from "@homero/pricing";
import { openNotificationWithIcon } from "src/components/utils/OpenNotificationWithIcon";

/**
 * The whole step as one card: how often, then the date and the arrival beside
 * it, then a sentence saying what was chosen (17 Sep redesign). How often used
 * to be a full-width row of heavy buttons above two loose columns, and read as
 * a leftover from step one rather than a question of this step.
 *
 * How often comes first because a plan changes what every day costs.
 */
/**
 * A discount landing, in the two places the customer can see it land: on the
 * badge of the day they just clicked, and on the line at the foot of the card
 * that says what it was worth.
 *
 * Both run once, both are short, and both are off entirely under
 * `prefers-reduced-motion`. The card is a pricing control, not a slot machine:
 * the motion is there to answer "did that do something?", and the answer is
 * finished being given in under half a second.
 */
const pop = keyframes`
  0%   { transform: scale(1); }
  45%  { transform: scale(1.12); }
  100% { transform: scale(1); }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ScheduleCard = styled.section`
  display: flex;
  flex-direction: column;
  border: 1px solid #e1e5ee;
  border-radius: 20px;
  background: #fff;

  .plan {
    padding: 22px 24px 20px;
    border-bottom: 1px solid #e9ecf3;
  }

  .plan h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    color: #101322;
  }

  .plan-note {
    display: block;
    margin-top: 10px;
    font-size: 13px;
    color: #575e75;
  }

  .schedule-body {
    display: grid;
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  }

  .schedule-body > * {
    padding: 22px 24px 26px;
  }

  .schedule-body > :first-child {
    border-right: 1px solid #e9ecf3;
  }

  @media (max-width: 991px) {
    .schedule-body {
      grid-template-columns: minmax(0, 1fr);
    }

    .schedule-body > :first-child {
      border-right: none;
      border-bottom: 1px solid #e9ecf3;
    }
  }

  /*
    On a phone the card's frame is dropped. The page already has its own side
    gutter, so a border plus 14px of padding inside it squeezed the plan
    buttons and the calendar into a column narrower than the screen for no
    gain. The sections keep their dividers and vertical rhythm.
  */
  @media (max-width: 560px) {
    border: none;
    border-radius: 0;
    background: transparent;

    .plan,
    .schedule-body > * {
      padding: 16px 0 18px;
    }

    .plan {
      padding-top: 0;
    }
  }
`;

/** The arrival column. */
const TimeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;

  .time-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .time-head strong {
    font-size: 17px;
    font-weight: 600;
    color: #101322;
  }

  .time-head span {
    font-size: 13px;
    color: #575e75;
  }
`;

/**
 * The date column, and the fix for the calendar's dead space.
 *
 * `.calendar` carries `height: 100%` from `index.css`, and the row stretches
 * every column to the tallest one. A month grid is a fixed size, so all that
 * stretching bought was a card whose border sat a couple of hundred pixels below
 * its last row of dates — most visibly next to a tall time column. Both columns
 * now size to their own content and the row aligns them at the top.
 *
 * Restyled to the funnel's own vocabulary rather than react-calendar's: the
 * panel radius and soft shadow of the address fields, 6px day tiles like every
 * other control, one arrow each way instead of four, and plain weekday names.
 * A cheaper day carries its saving on the tile, in green, so there is no dot to
 * decode and no legend to read.
 */
const DateColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;

  > p {
    margin: 0;
    font-weight: 500;
  }

  /* Inside the card now, so no border or shadow of its own. */
  .calendar {
    height: auto;
    min-height: 0;
    width: 100% !important;
    padding: 0;
    border: none !important;
    box-shadow: none;
  }

  .react-calendar__navigation {
    align-items: center;
    height: 36px;
    margin-bottom: 8px;
  }

  .react-calendar__navigation button {
    min-width: 0;
    background: none;
  }

  .react-calendar__navigation__arrow {
    flex: 0 0 32px;
    display: grid;
    place-items: center;
    height: 32px;
    padding: 0;
    border: 1px solid #e2e2e2 !important;
    border-radius: 6px;
    font-size: 0;
  }

  .react-calendar__navigation__arrow:enabled:hover {
    background: #f8f9ff !important;
    border-color: #c6ccff !important;
  }

  .react-calendar__navigation__label {
    font-size: 15px;
    font-weight: 600;
    color: #101322 !important;
  }

  .react-calendar__month-view__weekdays__weekday {
    padding: 4px 0 6px;
    font-size: 11px;
    font-weight: 500;
    text-transform: none;
    color: #6b7187;

    abbr {
      text-decoration: none;
      cursor: default;
    }
  }

  /*
    The page's own font and the 6px radius every other control uses; the
    calendar brought react-calendar's font and an 8px corner.
  */
  .react-calendar,
  .react-calendar button {
    font-family: inherit;
  }

  /* A 2px clear border is the gap between tiles, so tinted days never touch. */
  .react-calendar__tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    height: 48px;
    padding: 0;
    font-size: 13px;
    line-height: 16px;
    font-variant-numeric: tabular-nums;
    border: 2px solid transparent;
    border-radius: 6px;
    background-clip: padding-box;
  }

  .react-calendar__tile:enabled:hover {
    background-color: #f8f9ff;
  }

  /*
    Focus drawn as a ring, on every tile. react-calendar signals focus with a
    grey fill that a tinted (cheapest) day's own fill hid completely.
  */
  .react-calendar__tile:focus-visible {
    outline: 2px solid var(--blue-300);
    outline-offset: -2px;
  }

  /*
    The saving a day advertises. Green when the day is cheaper; grey (below)
    when the discount is real but the day still costs more than a normal one.
  */
  .day-save {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 10px;
    line-height: 12px;
    font-weight: 600;
    color: ${SAVINGS.ink};
    white-space: nowrap;
  }

  /*
    A discount that does not make the day cheaper: shown, so the calendar
    agrees with the time slots, but not in the green that means "cheaper".
    Before the active-tile rule below, which must still win on the blue day.
  */
  .day-save.muted {
    color: #3d4358;
  }

  /* The day just clicked, when clicking it earned something. */
  .day-save.pop {
    animation: ${pop} 220ms ease-out;
  }

  /*
    A calendar tile is 38px wide inside its padding on a phone. "$18 off" used
    to be measured with a percent mark in front of it, at 43px — it overflowed
    the tile and ran under the day beside it, so the word was dropped and the
    mark kept. The mark has since gone (it read as a percentage beside a dollar
    amount, which is two different claims about the same number), and without it
    "$18 off" measures ~32px, so the word stays on a phone too. A screen reader
    reads the full "save $18.29" either way.
  */
  @media (max-width: 560px) {
    .day-save {
      font-size: 9.5px;
      gap: 1px;
    }
  }

  /* The cheapest days: green, and only when some days cost more. */
  .react-calendar__tile.day-low {
    background-color: ${SAVINGS.tint};
    box-shadow: inset 0 0 0 1px ${SAVINGS.edge};
  }

  /*
    A day with a discount that does not make it cheaper: the same framed tile
    as the green days, in a neutral slate. Filled rather than bare so it cannot
    be mistaken for a disabled day, which is plain white and faded.
  */
  .react-calendar__tile.day-muted {
    background-color: #eef0f5;
    box-shadow: inset 0 0 0 1px #d9dde7;
  }

  .react-calendar__tile--active,
  .react-calendar__tile--active:enabled:hover {
    background-color: var(--blue-300) !important;
    box-shadow: none;
    color: #fff;
    font-weight: 600;
  }

  /*
    On the chosen (blue) day the green would fail against the fill, so both the
    text and the mark step up to the light tint that reads on it.
  */
  .react-calendar__tile--active .day-save,
  .react-calendar__tile--active .day-save svg {
    color: ${SAVINGS.onBlue};
  }

  .react-calendar__tile:disabled .day-save {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .day-save.pop {
      animation: none;
    }
  }
`;

/**
 * What was chosen, and what it costs, in one line — where the note box and the
 * "Cleaner will arrive…" sentence used to be two. The tone follows the price:
 * green when the choice saves, amber when it carries a fee, plain otherwise.
 */
const Summary = styled.div<{ tone: "save" | "fee" | "plain" }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px 24px;
  flex-wrap: wrap;
  padding: 18px 24px;
  border: 0 solid
    ${(p) => (p.tone === "save" ? SAVINGS.edge : p.tone === "fee" ? FEE.edge : "#e9ecf3")};
  border-top-width: 1px;
  border-radius: 0 0 20px 20px;
  background: ${(p) =>
    p.tone === "save" ? SAVINGS.tint : p.tone === "fee" ? FEE.tint : "#f7f9fd"};

  /* Its own rounded box once the card around it has no frame. */
  @media (max-width: 560px) {
    padding: 16px 14px;
    border-width: 1px;
    border-radius: 14px;
  }

  .when {
    display: block;
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
    color: #3b4252;
  }

  .when b {
    font-weight: 500;
    color: var(--blue-300);
  }

  .price {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-top: 2px;
    font-size: 13px;
    line-height: 1.5;
    color: ${(p) => (p.tone === "save" ? SAVINGS.ink : p.tone === "fee" ? FEE.ink : "#575e75")};
    animation: ${rise} 260ms ease-out;
  }

  /*
    The tick is the "applied" mark the note cannot make on its own: the sentence
    reads the same whether the discount is being announced for the first time or
    has been sitting there for a minute. Green filled circle, 14px, so it reads
    as a state and not as a button.
  */
  .price .applied {
    flex: none;
    display: grid;
    place-items: center;
    width: 14px;
    height: 14px;
    margin-top: 1px;
    border-radius: 50%;
    background: ${SAVINGS.mark};
    color: #fff;
    align-self: flex-start;
    animation: ${pop} 260ms ease-out;
  }

  .button-group {
    display: flex;
    gap: 10px;
  }

  .button-group > .next-button {
    margin: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .price,
    .price .applied {
      animation: none;
    }
  }
`;

/**
 * The card's closing sentence: what was chosen, in plain words, and why the
 * day costs more or less when area pricing is part of it.
 *
 * No totals (17 Sep redesign): the price belongs to the cart beside the step,
 * and a second total here was one more number to reconcile. What stays is the
 * reason — a nearby saving or a travel fee — because only this step can say it.
 */
function summaryFor(input: {
  cleanDate: string;
  arrival: string;
  occurance: number | string;
  /** The nearby discount inside the chosen day's price, and the travel fee. */
  discountCents: number;
  feeCents: number;
  /** A nearby discount this day offers at other times, when this time misses it. */
  missedCents: number | null;
  /** Whether any day in view is cheaper than others. */
  hasCheaperDays: boolean;
}): {
  tone: "save" | "fee" | "plain";
  plan: string;
  date: string;
  arrival: string;
  oneTime: boolean;
  note: string;
} {
  const { cleanDate, arrival, discountCents, feeCents } = input;
  const oneTime = (Number(input.occurance) || 0) === 0;
  const label = frequencyLabelFor(input.occurance);
  const plan = oneTime ? "One cleaning" : label === "Weekly" ? "Every week" : label;
  const date = cleanDate ? moment(cleanDate, "YYYY-MM-DD").format("dddd, MMM D") : "";
  const arrivalText = !arrival
    ? ""
    : arrival === "flexible"
    ? "between 9:00am and 4:00pm"
    : `at ${arrival}`;
  const base = { plan, date, arrival: arrivalText, oneTime };

  if (!cleanDate || !arrival) return { ...base, tone: "plain", note: "" };
  if (discountCents > 0) {
    return {
      ...base,
      tone: "save",
      // On a plan the saving is the first cleaning's alone — say so, or the
      // sentence reads as a saving on every one of them.
      note: `You save ${formatCents(discountCents)}${
        oneTime ? "" : " on your first cleaning"
      }. We’re already cleaning nearby.`,
    };
  }
  if (oneTime && feeCents > 0) {
    return {
      ...base,
      tone: "fee",
      note:
        `Includes a ${formatCents(feeCents)} travel fee: we have no other cleaning near you that day.` +
        (input.hasCheaperDays ? " Green days cost less." : ""),
    };
  }
  if (input.missedCents) {
    return {
      ...base,
      tone: "plain",
      note: `Pick a time with a green price to save ${formatCents(input.missedCents)}.`,
    };
  }
  return { ...base, tone: "plain", note: "" };
}

/**
 * The way in to Same-Day, at the foot of the ordinary step.
 *
 * An entry point rather than a control that lives alongside the calendar,
 * because taking it swaps the whole step: today cannot be booked with a date
 * picker and a static list of clock times, so it is not offered through them.
 *
 * Styled as a small offer in the same indigo the card on the other side of it
 * uses. It was an amber underlined link, which matched neither the funnel it
 * sits in nor the screen it leads to, and read as a warning about a thing the
 * customer had not done yet.
 */
const SameDayEntry = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 24px;
  padding: 14px 16px;
  border: 1px solid #e7e9f3;
  border-radius: 14px;
  background: linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%);
  font-family: Poppins, -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 560px) {
    flex-wrap: wrap;
  }
`;

/** The bolt, in a tile, so the row has a mark to start on rather than a lone glyph. */
const SameDayBolt = styled.span`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: #eaedff;
  color: var(--blue-300);
`;

const SameDayCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  strong {
    display: block;
    font-size: 14.5px;
    font-weight: 600;
    line-height: 1.35;
    color: #101322;
  }

  span {
    display: block;
    margin-top: 1px;
    font-size: 12.5px;
    line-height: 1.4;
    color: #6b7187;
  }
`;

const SameDayGo = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border: 1px solid #dde1ff;
  border-radius: 10px;
  background: #fff;
  color: var(--blue-300);
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 140ms ease, border-color 140ms ease;

  svg {
    transition: transform 180ms ease;
  }

  &:hover {
    background: #f2f4ff;
    border-color: #c6ccff;
  }

  &:hover svg {
    transform: translateX(2px);
  }

  &:focus-visible {
    outline: 2px solid var(--blue-300);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    svg,
    & {
      transition: none;
    }
  }
`;

/**
 * How often to re-ask whether Same-Day is still open.
 *
 * The set of windows narrows through the day. A customer who reached this step
 * at 2:55 and is still reading it at 3:05 was, until this, looking at an answer
 * that had since expired — with a closed window still selectable, and a booking
 * the server would refuse waiting on the other side of Next.
 */
const SAME_DAY_POLL_MS = 60_000;

const LoadingText = styled.div`
  display: flex;
  align-items: center;
  min-height: 80dvh;
  justify-content: center;
`;

export default function StepTwo(props) {
  const dispatch = useDispatch();
  const booking = useSelector((state: RootState) => state.bookingReducer);
  const {
    isFlexible,
    cleanDate,
    cleanTime,
    sameDay,
    sameDayMode,
    arrivalWindowLabel,
    types,
    zip,
    occurance,
    bedrooms,
    bathrooms,
    addOns,
    codeDiscount,
  } = booking;
  // Day prices come from the live config; re-render when it arrives.
  const { isLive: pricingIsLive } = usePricingConfig();
  /**
   * Whether the rates have settled either way. The default date waits on this:
   * the bundled fallback carries no surge, so choosing before the fetch lands
   * would take every day for a standard-price one and open on a Busy Day anyway.
   */
  const pricingSettled = pricingIsLive || pricingConfigLoadFailed();

  /**
   * Whether today can be picked at all.
   *
   * The calendar's floor has always been tomorrow. Same-Day moves it to today, but
   * only when the server says Same-Day is actually on offer — asked for today
   * specifically, because that is the only date the question means anything for.
   * With Same-Day off, `available` is false and the floor stays tomorrow, so this
   * step behaves exactly as it did before.
   *
   * The business's today, not the browser's: at 9pm in New York a customer in
   * London is already on tomorrow's date, and offering them "today" would mean a
   * different day than the one the server priced.
   */
  const businessToday = momentTz.tz("America/New_York").format("YYYY-MM-DD");

  /**
   * The earliest date an ordinary (non-Same-Day) booking may take, as a `Date`
   * the calendar can use for `minDate`.
   *
   * Derived from the business's today rather than the browser's, which is the
   * whole point. `minDate` was `new Date().setDate(getDate() + 1)` — the
   * customer's tomorrow. At 10pm Pacific that is the 31st, while New York is
   * already *on* the 31st, so the calendar offered a date the server considers
   * today. The customer picked it as an ordinary booking, filled in everything,
   * and only at `/bookings/initialize` were they told a cleaning today has to be
   * booked as Same-Day.
   *
   * Built with the local-midnight constructor rather than `new Date(string)`,
   * which parses a bare date as UTC midnight and so renders as the day before in
   * every timezone west of Greenwich — the same class of bug, one layer down.
   */
  const businessTomorrow = useMemo(() => {
    const [year, month, day] = momentTz
      .tz("America/New_York")
      .add(1, "day")
      .format("YYYY-MM-DD")
      .split("-")
      .map(Number);
    return new Date(year, month - 1, day);
  }, [businessToday]); // eslint-disable-line react-hooks/exhaustive-deps
  /**
   * One request, read by both surfaces on this step.
   *
   * The calendar needs it to unlock today's tile and the card needs it to list
   * the windows, and they used to ask separately — this hook is deliberately
   * uncached (the answer narrows through the day), so two callers meant two
   * round trips whose answers could differ, and a cutoff passing between them
   * would have shown windows on a tile that had just re-locked.
   *
   * Asked about today rather than about `cleanDate`, because "today" is the only
   * date the question means anything for; a different selected date simply does
   * not show the card.
   */
  const {
    answer: sameDayAnswer,
    loading: sameDayLoading,
    available: sameDayAvailable,
  } = useSameDayAvailability(
    businessToday,
    resolveTypeId(types),
    SAME_DAY_POLL_MS
  );

  /**
   * Whether to offer the way in to Same-Day.
   *
   * `available` already means "and there is at least one window left", which is
   * the difference between offering a choice and offering a door into an empty
   * room. Never while Same-Day is already showing, either — the way back out is
   * the link inside it.
   */
  const canOfferSameDay = sameDayAvailable && !sameDayMode;

  /**
   * The latest choice, for the schedule callbacks: they fire from a poll, long
   * after the render that created them.
   */
  const choice = useRef({ cleanDate, cleanTime, isFlexible, sameDayMode });
  choice.current = { cleanDate, cleanTime, isFlexible, sameDayMode };

  /**
   * Blocked days and times, kept current (17 Sep feedback). When the admin
   * blocks what this customer picked, the pick is cleared and a small notice
   * says why; when they block something else, the notice is the scarcity cue
   * the business asked for. One `key`, so notices replace rather than stack.
   */
  const { bookedDates, loadingBookedDates, bookedTimes, loadingBookedTimes } =
    useLiveSchedule(cleanDate, {
      onDatesBlocked: (added) => {
        if (choice.current.sameDayMode) return;
        if (added.includes(choice.current.cleanDate)) {
          // Empty, so the effect below moves them to the next open day.
          dispatch(setDate(""));
          message.warning({
            key: "schedule",
            content: "This day is no longer available. Please pick another day.",
          });
        } else {
          message.info({ key: "schedule", content: "Our schedule is filling up quickly." });
        }
      },
      onTimesBlocked: (date, added) => {
        const { cleanDate, cleanTime, isFlexible, sameDayMode } = choice.current;
        if (sameDayMode || date !== cleanDate) return;
        if (!isFlexible && cleanTime && disabledTime(cleanTime, added)) {
          dispatch(setTime(""));
          message.warning({
            key: "schedule",
            content: "This time slot is no longer available. Please pick another time.",
          });
        } else {
          message.info({ key: "schedule", content: "Our schedule is filling up quickly." });
        }
      },
    });

  useEffect(() => {
    /**
     * Start on the next open day as soon as we know which days are open —
     * including when none are booked. This waited for `bookedDates?.length`,
     * and an empty list is falsy, so on a quiet week the step opened on an
     * empty calendar with every time greyed out. If the list could not be
     * fetched at all, tomorrow is the best guess; the server refuses a blocked
     * day at checkout either way.
     */
    if (!cleanDate && !loadingBookedDates && pricingSettled) {
      /**
       * And not on a Busy Day, when a day at the standard price is near: the
       * first total the customer reads should be the baseline one, not the
       * week's dearest (26 Sep feedback). Choosing a Busy Day is still a click.
       */
      const { dayOfWeekSurgePercent } = currentPricingConfig();
      const isBusyDay = (date: string) =>
        dayOfWeekSurgePercent[dayOfWeekForServiceDate(date)] > 0;
      const date = moment(
        getDefaultBookingDate(bookedDates ?? [], isBusyDay)
      ).format("YYYY-MM-DD");
      dispatch(setDate(date));
    }
  }, [bookedDates, loadingBookedDates, pricingSettled, cleanDate, dispatch]);

  const width = useResizeObserver();

  const isMobile = width <= 500;

  /**
   * Area pricing for this ZIP: which days are cheaper or dearer, and at what
   * times. Folded into each day's price below, not shown on its own.
   */
  const { calendar } = useAreaCalendar(zip);
  const areaDays = useMemo(
    () => (calendar?.status === "served" ? calendar.days : {}),
    [calendar]
  );
  const chosenDay: AreaDay | undefined = cleanDate ? areaDays[cleanDate] : undefined;
  const oneTime = (Number(occurance) || 0) === 0;
  const arrival = arrivalFor({ isFlexible, cleanTime });
  const configVersion = pricingConfigVersion();

  /** The booking's price inputs, for pricing a day that is not the chosen one. */
  const priceInputs = useMemo(
    () => ({ bedrooms, bathrooms, types, occurance, isFlexible, codeDiscount, addOns }),
    [bedrooms, bathrooms, types, occurance, isFlexible, codeDiscount, addOns]
  );

  /** The Flexible Time discount on the chosen day, from the config — not a hardcoded $10. */
  const flexibleCents = useMemo(() => {
    try {
      return computeBookingPrice({ ...priceInputs, occurance: 0, isFlexible: true, cleanDate })
        .flexibleDiscountCents;
    } catch {
      return 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceInputs, cleanDate, configVersion]);

  /**
   * The nearby discount on the chosen day, from the engine: what a one-time
   * arrival inside the day's nearby blocks saves.
   */
  const nearbySavingCents = useMemo(() => {
    if (!cleanDate || chosenDay?.tier !== "cluster" || calendar?.status !== "served") return null;
    try {
      return (
        computeBookingPrice({
          ...priceInputs,
          /*
            The plan as chosen, not forced to One Time. Plans earn the nearby
            discount now (on their first cleaning), and the discount is a
            percentage of a base the plan's own frequency discount has already
            moved — so pricing this at One Time printed a saving a customer on
            Weekly would never see.
          */
          cleanDate,
          proximityTier: "cluster",
          proximityPercent: calendar.clusterPercent ?? 0,
          proximityFor: undefined,
        }).proximityDiscountCents || null
      );
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceInputs, cleanDate, chosenDay, calendar, configVersion]);

  /**
   * What an arrival saves on the chosen day: the nearby discount, at the
   * percentage the arrival's time span sets (0% hides it) or the normal one.
   *
   * Priced at the tier *this* slot earns, not the one the store holds for the
   * selected arrival, in one engine call so the number on the button is the
   * number charged. Plans included — the saving lands on their first cleaning,
   * which is the one this step is choosing a time for.
   *
   * Memoised per time: the picker asks for every slot on every render.
   */
  const savingFor = useMemo(() => {
    const cache = new Map<string, number | null>();
    return (time: string): number | null => {
      if (!cleanDate || tierForDay(chosenDay, time) !== "cluster") return null;
      if (!cache.has(time)) {
        let saving: number | null = null;
        try {
          saving =
            computeBookingPrice({
              ...priceInputs,
              cleanDate,
              cleanTime: time,
              isFlexible: time === "flexible",
              proximityTier: "cluster",
              proximityPercent: percentForTier(calendar, "cluster"),
              proximityFor: undefined,
            }).proximityDiscountCents || null;
        } catch {
          saving = null;
        }
        cache.set(time, saving);
      }
      return cache.get(time) ?? null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceInputs, cleanDate, chosenDay, calendar, configVersion]);

  /** The month on screen, so only its days are priced. */
  const [month, setMonth] = useState(() =>
    moment(cleanDate || businessTomorrow).format("YYYY-MM")
  );
  useEffect(() => {
    if (cleanDate) setMonth(cleanDate.slice(0, 7));
  }, [cleanDate]);
  const maxDate = useMemo(() => new Date(new Date().setMonth(new Date().getMonth() + 2)), []);

  /** Every pickable day in view, priced for this booking at the chosen arrival. */
  const dayPrices = useMemo(() => {
    const prices: Record<string, number | null> = {};
    const [year, monthIndex] = month.split("-").map(Number);
    const floor = moment(businessTomorrow).format("YYYY-MM-DD");
    const ceiling = moment(maxDate).format("YYYY-MM-DD");
    // The area calendar answers for today and the 61 days after it. A day past
    // that has no known area price, so it shows none rather than looking
    // cheapest for want of a travel fee.
    const areaEnd = momentTz.tz("America/New_York").add(61, "days").format("YYYY-MM-DD");
    const daysInMonth = new Date(year, monthIndex, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${month}-${String(day).padStart(2, "0")}`;
      if (date < floor || date > ceiling || bookedDates?.includes(date)) continue;
      prices[date] =
        calendar?.status === "served" && date > areaEnd
          ? null
          : priceOnDay(priceInputs, date, arrival, calendar);
    }
    return prices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, businessTomorrow, maxDate, bookedDates, priceInputs, arrival, calendar, configVersion]);
  const lowDays = useMemo(() => cheapestDays(dayPrices), [dayPrices]);

  /**
   * What each day advertises, in cents — the calendar's whole output now that it
   * no longer prints totals.
   *
   * Keyed off the same day list as `dayPrices` so the two cannot disagree about
   * which days are pickable, but priced at the day's best arrival rather than
   * the selected one: see `daySavingBadges`.
   */
  const daySavings = useMemo(
    () => daySavingBadges(priceInputs, Object.keys(dayPrices), calendar),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dayPrices, priceInputs, calendar, configVersion]
  );

  /**
   * The chosen day as the cart prices it: the server's quote once it answers
   * for this date and time, the area calendar until then — the same data the
   * quote is resolved from, so the two agree.
   */
  const settled =
    booking.proximityFor !== "" &&
    booking.proximityFor === quoteKey(booking) &&
    !booking.proximityPending;
  const chosen = (() => {
    if (!cleanDate) return null;
    try {
      if (settled) return computeBookingPrice(booking);
      const tier = tierForDay(chosenDay, arrival);
      return computeBookingPrice({
        ...booking,
        proximityTier: tier,
        proximityPercent: percentForTier(calendar, tier),
        proximityFor: undefined,
      });
    } catch {
      return null;
    }
  })();

  const summary = summaryFor({
    cleanDate,
    arrival,
    occurance,
    // The figure the selected slot's button prints: the same engine field.
    discountCents: chosen?.proximityDiscountCents ?? 0,
    feeCents: chosen?.travelPremiumCents ?? 0,
    missedCents:
      chosenDay?.tier === "cluster" && !savingFor(arrival) ? nearbySavingCents : null,
    hasCheaperDays: lowDays.size > 0,
  });

  /** How long the chosen cleaning takes, as step one printed it. */
  const duration = cleaningTypes.find((t) => t.value === resolveTypeId(types))?.subText;

  const plans = offersPlans(types);
  const planNote = oneTime
    ? "One cleaning, no plan."
    : `We come back ${summary.plan.toLowerCase()}. ${
        frequencyOptions.find((o) => o.value === Number(occurance))?.tag?.replace("Save ", "") ?? ""
      } off each cleaning.`;

  const next = () => {
    /**
     * A ZIP we do not clean in stops here as it does on step 1. The header
     * chip (`ZipChip`) lets the ZIP change on this step, so without this a
     * customer could switch to a refused ZIP and carry on to the address step.
     */
    if (calendar?.status === "refused") {
      dispatch(setZipEditorOpen(true));
      return;
    }
    /**
     * A blocked choice first. These checks used to sit after the "all chosen"
     * branch below, which every chosen time passes, so they never ran.
     */
    if (!sameDay && cleanDate && bookedDates?.includes(cleanDate)) {
      openNotificationWithIcon(
        "error",
        "Selected date is not available",
        "Please select another date"
      );
      return;
    }
    if (
      !sameDay &&
      !isFlexible &&
      cleanTime &&
      bookedTimes &&
      disabledTime(cleanTime, bookedTimes)
    ) {
      openNotificationWithIcon(
        "error",
        "Selected time is not available",
        "Please select another time"
      );
      return;
    }

    // A chosen Same-Day window is a complete time choice, like a fixed time or
    // Flexible. Without this the customer would pick a window and then be told to
    // pick a time.
    if (
      (cleanDate && isFlexible) ||
      (cleanDate && cleanTime) ||
      (cleanDate && sameDay)
    ) {
      logEvent("User picked a cleaning time", {
        cleanDate,
        cleanTime: sameDay ? "same-day" : isFlexible ? "flexible" : cleanTime,
      });
      props.changePage(3);
    } else {
      let notification = { description: "", title: "" };
      if (!cleanDate && !cleanTime && !isFlexible) {
        notification.description = "Please select the date and time";
        notification.title = "Error";
      } else if (!cleanDate) {
        notification.title = "Please select a date";
        notification.description = "Please select a date for your cleaning";
      } else if (!cleanTime) {
        notification.title = "Please select a time";
        notification.description = "Please select a time for your cleaning";
      }
      logEvent("User forgot to select a proper time", {
        cleanDate,
        cleanTime: isFlexible ? "flexible" : cleanTime,
      });
      openNotificationWithIcon(
        "error",
        notification.title,
        notification.description
      );
    }
  };

  const prev = () => {
    props.changePage(1);
  };

  const navButtons = (className: string) =>
    isMobile ? null : (
      <div className={className}>
        <Button onClick={prev} type="primary" className="next-button back-button">
          Back
        </Button>
        <Button onClick={next} type="primary" className="next-button">
          Next
        </Button>
      </div>
    );

  if (loadingBookedDates) {
    // show loading state
    return (
      <LoadingText>
        <p>Loading available schedules...</p>
      </LoadingText>
    );
  }

  return (
    <Layout className="step-layout mobile-step-layout">
      <h1 className="semi-bold">When should we clean?</h1>
      <br />
      {/*
        Same-Day replaces this step rather than adding to it. The calendar's floor
        is tomorrow and the time grid's slots are a static 8:00am-to-6:00pm list
        with no notion of "now", so neither control can correctly describe today —
        at 3pm the grid would offer to arrive at 8 that morning. Today is reachable
        only through the mode, where the server's windows are the only times shown.
      */}
      {sameDayMode ? (
        <SameDayWindows
          answer={sameDayAnswer}
          loading={sameDayLoading}
          serviceDate={businessToday}
        />
      ) : (
        <>
          <ScheduleCard aria-label="When should we clean">
            {/*
              Plans exist only for the cleaning types that can recur, and not
              inside Same-Day, which is One Time only (`enter_same_day`) — this
              branch is not rendered there at all.
            */}
            {plans ? (
              <div className="plan">
                <h2>How often</h2>
                <PlanPicker
                  value={occurance}
                  onChange={(value) => {
                    dispatch(setOccurance(value));
                    dispatch(setPromocode("", 0));
                  }}
                />
                <span className="plan-note">{planNote}</span>
              </div>
            ) : null}
            <div className="schedule-body">
                <DateColumn>
                  <Calendar
                    calendarType="US"
                    value={
                      cleanDate
                        ? // Local midnight on the chosen day. New York's midnight
                          // is the day before for anyone west of it, which
                          // highlighted the day before the one they picked.
                          moment(cleanDate, "YYYY-MM-DD").toDate()
                        : undefined
                    }
                    tileDisabled={({ date }) =>
                      bookedDates?.includes(
                        moment(date).format("YYYY-MM-DD")
                      ) || false
                    }
                    onChange={(v, e) =>
                      dispatch(setDate(moment(v).format("YYYY-MM-DD")))
                    }
                    /*
                      Tomorrow, always. Same-Day briefly moved this floor to today,
                      which put a selectable tile in front of the one pair of
                      controls that cannot describe today: the grid's slots are a
                      static list with no notion of "now". Today now belongs to the
                      Same-Day mode, which shows only the windows the server says
                      are still reachable.
                    */
                    minDate={businessTomorrow}
                    maxDate={
                      new Date(new Date().setMonth(new Date().getMonth() + 2))
                    }
                    className="calendar"
                    next2Label={null}
                    prev2Label={null}
                    prevLabel={<ChevronLeft size={16} aria-hidden="true" color="var(--blue-300)" />}
                    nextLabel={<ChevronRight size={16} aria-hidden="true" color="var(--blue-300)" />}
                    prevAriaLabel="Previous month"
                    nextAriaLabel="Next month"
                    formatShortWeekday={(_, date) => moment(date).format("ddd")}
                    showNeighboringMonth={false}
                    activeStartDate={moment(`${month}-01`, "YYYY-MM-DD").toDate()}
                    onActiveStartDateChange={({ activeStartDate }) =>
                      setMonth(moment(activeStartDate).format("YYYY-MM"))
                    }
                    tileClassName={({ date, view }) => {
                      if (view !== "month") return null;
                      const day = moment(date).format("YYYY-MM-DD");
                      if (lowDays.has(day)) return "day-low";
                      return daySavings.grey[day] !== undefined ? "day-muted" : null;
                    }}
                    /*
                      The incentive, where there is one, and nothing at all where
                      there is not — in place of a dollar total on all thirty
                      days. A calendar of totals asks the customer to compare
                      thirty numbers before picking a Tuesday; this asks them to
                      notice three. The exact price for the date and arrival they
                      choose is one step away, in the cart beside them.

                      Green only where the day ends up cheaper; a surged day with
                      a real discount gets the same badge in grey — see
                      `daySavingBadges`.
                    */
                    tileContent={({ date, view }) => {
                      if (view !== "month") return null;
                      const day = moment(date).format("YYYY-MM-DD");
                      const green = daySavings.green[day];
                      const cents = green ?? daySavings.grey[day];
                      if (typeof cents !== "number") return null;
                      return (
                        <span
                          /* Pops on the day just chosen — see `pop` above. */
                          className={[
                            "day-save",
                            green === undefined ? "muted" : "",
                            day === cleanDate ? "pop" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          /* The exact cents to a screen reader; the tile rounds
                             down, and a number read aloud can afford to be whole. */
                          aria-label={`, save ${formatCents(cents)}`}
                        >
                          {tileSaving(cents)}
                          <span className="day-save-word">&nbsp;off</span>
                        </span>
                      );
                    }}
                  />
                </DateColumn>
                <TimeColumn>
                  <div className="time-head">
                    <strong>
                      {cleanDate
                        ? moment(cleanDate, "YYYY-MM-DD").format("dddd, MMMM D")
                        : "Arrival time"}
                    </strong>
                    {duration ? <span>Takes {duration.replace("-", "–")}</span> : null}
                  </div>
                  <ArrivalPicker
                    cleanTime={cleanTime}
                    isFlexible={Boolean(isFlexible)}
                    disabled={!cleanDate}
                    loading={loadingBookedTimes}
                    bookedTimes={bookedTimes ?? []}
                    flexibleCents={flexibleCents}
                    savingCents={savingFor}
                    onTime={(time) => dispatch(setTime(time))}
                    onFlexible={() => dispatch(setFlexibleTime())}
                  />
                </TimeColumn>
            </div>
            <Summary tone={summary.tone} data-testid="arrival-summary">
              <div role="status" aria-live="polite">
                <p className="when">
                  {!summary.date ? (
                    "Pick a date and an arrival time."
                  ) : (
                    <>
                      <b>{summary.plan}</b>
                      {summary.oneTime ? " on " : ", starting "}
                      <b>{summary.date}</b>
                      {summary.arrival ? (
                        <>
                          , arriving <b>{summary.arrival}</b>.
                        </>
                      ) : (
                        ". Pick an arrival time."
                      )}
                    </>
                  )}
                </p>
                {summary.note ? (
                  /*
                    Keyed on the sentence so that a new one remounts and plays
                    its entrance again. Without the key React reuses the node,
                    the text changes in place, and the discount that was just
                    applied arrives with no more ceremony than a typo fix.
                  */
                  <span className="price" key={summary.note}>
                    {summary.tone === "save" ? (
                      <span className="applied" aria-hidden="true">
                        <Check size={10} strokeWidth={3.5} />
                      </span>
                    ) : null}
                    <span>{summary.note}</span>
                  </span>
                ) : null}
              </div>
              {navButtons("button-group")}
            </Summary>
          </ScheduleCard>
          {canOfferSameDay ? (
            <SameDayEntry>
              <SameDayBolt aria-hidden="true">
                <Zap size={16} fill="currentColor" />
              </SameDayBolt>
              <SameDayCopy>
                <strong>Need it today?</strong>
                <span>We still have arrival windows open for today.</span>
              </SameDayCopy>
              <SameDayGo
                type="button"
                onClick={() => dispatch(enterSameDay(businessToday))}
              >
                Book as Same Day
                <ArrowRight size={15} aria-hidden="true" />
              </SameDayGo>
            </SameDayEntry>
          ) : null}
        </>
      )}
      {sameDayMode ? (
        <>
          {sameDay ? (
            <>
              <br />
              <span className="center">
                Cleaner will arrive today, {typesetWindowLabel(arrivalWindowLabel)}.
                {/* Same-Day is one-time, so the area price applies to it too. */}
                {chosen && chosen.travelPremiumCents > 0
                  ? ` Includes a ${formatCents(chosen.travelPremiumCents)} travel fee: we have no other cleaning near you today.`
                  : chosen && chosen.proximityDiscountCents > 0
                  ? ` You save ${formatCents(chosen.proximityDiscountCents)}. We’re already cleaning nearby.`
                  : ""}
              </span>
            </>
          ) : null}
          <br />
          {navButtons("button-group center")}
        </>
      ) : null}
      {isMobile ? <MobileNavbar nextAction={next} prevAction={prev} /> : null}
      <br />
    </Layout>
  );
}
